import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDonation, setIsDonation] = useState(false);
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setError('No payment session found');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/stripe/session-status?session_id=${sessionId}`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          }
        );

        if (response.data.payment_status === 'paid') {
          // Check if this was a donation (backend stores as 'is_donation')
          const metadata = response.data.metadata || {};
          const isDonationFlag = metadata.is_donation === 'true' || metadata.is_donation === true;
          setIsDonation(isDonationFlag);
          
          console.log('Payment metadata:', metadata);
          console.log('Is donation:', isDonationFlag);
          
          // Reload user data to get updated premium status
          await loadUser();
          setLoading(false);
        } else {
          setError('Payment not completed');
          setLoading(false);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Failed to verify payment');
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, loadUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-navy-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Verifying Payment...</h2>
          <p className="text-gray-600 mt-2">Please wait while we confirm your subscription</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-mint-500 hover:bg-mint-600 text-white"
            >
              Return to Pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-4">
      <Card className="max-w-2xl w-full">
        <CardContent className="p-8">
          <div className="text-center">
            {/* Success Icon */}
            <div className="relative inline-block mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Success Message */}
            {isDonation ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  JazakAllah Khair! 🤲
                </h1>
                <p className="text-xl text-navy-500 font-semibold mb-2">
                  Thank you for your generous donation
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  May Allah accept your contribution and multiply your rewards
                </p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  JazakAllah Khair! 🤲
                </h1>
                <p className="text-xl text-navy-500 font-semibold mb-2">
                  Thank you for your support
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  Welcome to qbool Premium! May Allah bless your journey to find your perfect match
                </p>
              </>
            )}

            {/* Benefits - Only show for subscription */}
            {!isDonation && (
            <div className="bg-white rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-lg mb-4 text-center">Your Premium Benefits:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Unlimited profile views</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Send unlimited messages</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">See who viewed your profile</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Advanced search filters</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Priority customer support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Verified premium badge</span>
                </li>
              </ul>
            </div>
            )}

            {/* Sadaqah Jariyah message for donations */}
            {isDonation && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 mb-6 border border-green-200">
                <h3 className="font-semibold text-lg mb-3 text-center text-green-800">Sadaqah Jariyah - Continuous Reward</h3>
                <p className="text-gray-700 text-center leading-relaxed">
                  Every couple that meets and marries through qbool, you share in the reward. 
                  Your contribution helps families unite in halal ways. May Allah accept your donation 
                  and make it a means of ongoing blessings for you and your family.
                </p>
              </div>
            )}

            {/* Call to Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/search')}
                className="bg-mint-500 hover:bg-mint-600 text-white px-8"
              >
                Start Searching
              </Button>
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="border-mint-500 text-navy-500 hover:bg-cream-100 px-8"
              >
                View My Profile
              </Button>
            </div>

            {/* Dua */}
            {isDonation ? (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-6 border border-purple-100">
                <p className="text-sm text-gray-700 italic text-center">
                  "مَا نَقَصَتْ صَدَقَةٌ مِنْ مَالٍ"
                </p>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  "Charity does not decrease wealth" - Prophet Muhammad ﷺ
                </p>
                <p className="text-xs text-gray-600 mt-2 text-center font-semibold">
                  May Allah bless you and increase your provisions
                </p>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-4 mb-6 border border-red-100">
                <p className="text-sm text-gray-700 italic text-center">
                  "رَبَّنَا هَبْ لَنَا مِنْ أَزْوَاجِنَا وَذُرِّيَّاتِنَا قُرَّةَ أَعْيُنٍ"
                </p>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  May Allah grant you a righteous spouse who brings comfort to your eyes
                </p>
              </div>
            )}

            {/* Receipt Info */}
            <p className="text-xs text-gray-500 mt-4">
              A receipt has been sent to your email. You can manage your subscription anytime from your profile settings.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
